import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Headphones, AlertCircle, CheckCircle, LogIn } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const supportCategories = [
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'adobe', label: 'Adobe' },
  { value: '3cx', label: '3CX' },
  { value: 'kaspersky', label: 'Kaspersky' },
  { value: 'ekahau', label: 'Ekahau' },
  { value: 'network', label: 'Network & Infrastructure' },
  { value: 'other', label: 'Other' },
];

const priorityLevels = [
  { value: 'low', label: 'Low', color: '#16A34A' },
  { value: 'medium', label: 'Medium', color: '#D97706' },
  { value: 'high', label: 'High', color: '#DC2626' },
  { value: 'critical', label: 'Critical', color: '#7C2D12' },
];

export default function TechnicalSupportPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [form, setForm] = useState({ category: '', subject: '', priority: 'medium', description: '', device_info: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/quotes`, {
        product_category: 'support',
        product_name: `Support: ${form.category} - ${form.subject}`,
        quantity: 1,
        details: `Priority: ${form.priority}\nCategory: ${form.category}\nSubject: ${form.subject}\nDescription: ${form.description}\nDevice Info: ${form.device_info}`,
        contact_email: user?.email || '',
        contact_phone: '',
        company_name: user?.company || '',
      });
      setSubmitted(true);
    } catch (err: any) {
      setError('Failed to submit support request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '12px 14px', border: '1px solid var(--border)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, background: 'white', fontFamily: 'var(--font-body)' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' };

  return (
    <>
      <Header />
      <main style={{ paddingTop: 72, minHeight: '100vh', background: 'var(--bg-section)' }}>
        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, #B71C1C 0%, #D32F2F 100%)', padding: '64px 24px', textAlign: 'center' }}>
          <Headphones size={40} style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 16 }} strokeWidth={1.5} />
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, color: 'white', marginBottom: 12 }}>
            {t('services.support.title')}
          </h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.8)', maxWidth: 500, margin: '0 auto' }}>{t('services.support.desc')}</p>
        </div>

        <div style={{ maxWidth: 700, margin: '0 auto', padding: '48px 24px' }}>
          {!user ? (
            /* Not logged in */
            <div data-testid="support-login-required" style={{ background: 'white', border: '1px solid var(--border)', padding: '64px 40px', textAlign: 'center' }}>
              <LogIn size={48} style={{ color: 'var(--primary)', marginBottom: 20, opacity: 0.6 }} />
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Login Required</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 15, marginBottom: 32, maxWidth: 400, margin: '0 auto 32px' }}>
                Only registered customers can request technical support. Please login with your customer account to submit a support ticket.
              </p>
              <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
                <Link to="/login" data-testid="support-login-btn" style={{ padding: '14px 32px', background: 'var(--primary)', color: 'white', fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t('auth.login')}
                </Link>
                <Link to="/register" data-testid="support-register-btn" style={{ padding: '14px 32px', border: '2px solid var(--primary)', color: 'var(--primary)', fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t('auth.register')}
                </Link>
              </div>
            </div>
          ) : submitted ? (
            /* Success */
            <div data-testid="support-success" style={{ background: 'white', border: '1px solid var(--border)', padding: '64px 40px', textAlign: 'center' }}>
              <CheckCircle size={56} style={{ color: 'var(--primary)', marginBottom: 20 }} />
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Support Request Submitted</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 15, marginBottom: 8 }}>Your ticket has been created. Our team will respond shortly.</p>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-muted)', marginBottom: 32 }}>Customer ID: {user.id}</p>
              <button onClick={() => { setSubmitted(false); setForm({ category: '', subject: '', priority: 'medium', description: '', device_info: '' }); }}
                style={{ padding: '14px 32px', background: 'var(--primary)', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                New Request
              </button>
            </div>
          ) : (
            /* Support Form */
            <div style={{ background: 'white', border: '1px solid var(--border)', padding: '40px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24, padding: '12px 16px', background: 'rgba(13,71,161,0.06)', border: '1px solid rgba(13,71,161,0.12)' }}>
                <AlertCircle size={18} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Logged in as: {user.name}</p>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Customer ID: {user.id} | {user.email}</p>
                </div>
              </div>

              {error && <div style={{ padding: '12px', background: 'rgba(211,47,47,0.08)', color: 'var(--secondary)', marginBottom: 16, fontSize: 14 }}>{error}</div>}

              <form onSubmit={handleSubmit} data-testid="support-form">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div>
                    <label style={labelStyle}>Support Category *</label>
                    <select data-testid="support-category" value={form.category} onChange={e => setForm({...form, category: e.target.value})} required style={{...inputStyle, cursor: 'pointer'}}>
                      <option value="">Select category...</option>
                      {supportCategories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Subject *</label>
                    <input data-testid="support-subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} required placeholder="Brief description of the issue" style={inputStyle} />
                  </div>

                  <div>
                    <label style={labelStyle}>Priority</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {priorityLevels.map(p => (
                        <button key={p.value} type="button" data-testid={`priority-${p.value}`} onClick={() => setForm({...form, priority: p.value})}
                          style={{ flex: 1, padding: '10px 8px', fontSize: 12, fontWeight: 600, border: `2px solid ${form.priority === p.value ? p.color : 'var(--border)'}`, background: form.priority === p.value ? `${p.color}14` : 'white', color: form.priority === p.value ? p.color : 'var(--text-secondary)', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Description *</label>
                    <textarea data-testid="support-description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required rows={6} placeholder="Describe the issue in detail..." style={{...inputStyle, resize: 'vertical' as const}} />
                  </div>

                  <div>
                    <label style={labelStyle}>Device / System Info</label>
                    <input data-testid="support-device" value={form.device_info} onChange={e => setForm({...form, device_info: e.target.value})} placeholder="e.g., Windows 11, Office 365, etc." style={inputStyle} />
                  </div>

                  <button type="submit" data-testid="support-submit-btn" disabled={loading}
                    style={{ padding: '16px', background: 'var(--secondary)', color: 'white', fontWeight: 700, fontSize: 15, border: 'none', textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Headphones size={18} /> Submit Support Request
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
